import { FC, useEffect, useState } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import { StepHeader, Layout, NextButton } from '@eneco/flows/components';
import { useFlowHooks } from '@eneco/flows/utils/FlowProvider';
import { typedYupResolver } from '@eneco/hooks/form';
import { Grid, NotificationBox, RadioGroup, RadioTile, Stack, Stretch, Text } from '@eneco/sparky';
import { usePlaceholderContent } from '@sitecore/common';
import { NotificationField, SituationStepRendering } from '@sitecore/types/SituationStep';
import { useForm, Controller } from 'react-hook-form';
import * as yup from 'yup';

import { FlowConsumerContextEnergy } from '../../../context';
import { useFlowNavigation } from '../../hooks';

interface FormFields {
  isMoving: string;
  hasEnergy: string;
}

export const SituationStep: FC = () => {
  const { useFlowSelector } = useFlowHooks<FlowConsumerContextEnergy>();
  const flowState = useFlowSelector(state => state);
  const { updateContextAndGoToNextStep } = useFlowNavigation();

  const {
    context: { isMoving, hasEnergy },
  } = flowState;

  const { SituationStep: { fields: textualData } = {} } = usePlaceholderContent<{
    SituationStep: SituationStepRendering;
  }>();

  const formSchema = yup.object({
    isMoving: yup.string().test({ message: 'invalid', test: value => value !== 'false' }),
    hasEnergy: yup.string().required(textualData?.choice?.hasEnergyRadio?.value?.requiredMessage),
  });

  const {
    handleSubmit,
    control,
    getValues,
    watch,
    formState: { isSubmitSuccessful },
  } = useForm<FormFields>({
    mode: 'onSubmit',
    defaultValues: {
      isMoving: isMoving?.toString(),
      hasEnergy: hasEnergy?.toString(),
    },
    resolver: typedYupResolver<FormFields>(formSchema),
  });
  const [notificationField, setNotificationField] = useState<NotificationField | undefined>(undefined);
  const [canContinueFlow, setCanContinueFlow] = useState(true);

  const mutatedIsMoving = watch('isMoving');
  const mutatedHasEnergy = watch('hasEnergy');

  useEffect(() => {
    if (isSubmitSuccessful) {
      const formData = getValues();
      updateContextAndGoToNextStep({
        isMoving: formData.isMoving === 'true',
        hasEnergy: formData.hasEnergy === 'true',
      });
    }
  }, [isSubmitSuccessful]);

  useEffect(() => {
    const isCustomerMoving = mutatedIsMoving === 'true';
    const hasEnecoEnergy = mutatedHasEnergy === 'true';
    if (!mutatedIsMoving && !mutatedHasEnergy) return;
    else if (isCustomerMoving && !hasEnecoEnergy) {
      setNotificationField(undefined);
      setCanContinueFlow(true);
    } else if (!isCustomerMoving && hasEnecoEnergy) {
      setNotificationField(textualData?.notifications?.doesNotMoveOutAndHasEnergyNotification);
      setCanContinueFlow(false);
    } else if (!isCustomerMoving && !hasEnecoEnergy) {
      setNotificationField(textualData?.notifications?.doesNotMoveOutAndHasNoEnergyNotification);
      setCanContinueFlow(false);
    } else if (isCustomerMoving && hasEnecoEnergy) {
      setNotificationField(textualData?.notifications?.doesMoveAndHasEnergyNotification);
      setCanContinueFlow(true);
    }
  }, [mutatedIsMoving, mutatedHasEnergy]);

  return (
    <Layout.Content variant="A" handleSubmit={handleSubmit}>
      <StepHeader textualData={textualData} />
      <Controller
        control={control}
        name="isMoving"
        render={({ field: { onChange, value, name } }) => (
          <RadioGroup
            name={name}
            direction="row"
            label={textualData?.choice?.isMovingRadio?.value?.label ?? ''}
            value={value}
            onValueChange={onChange}>
            <Stretch width={true}>
              <Grid gridTemplateColumns="1fr 1fr" gap={'3'}>
                {textualData?.choice?.isMovingRadio?.value?.options?.map(option => (
                  <RadioTile key={option.label} value={option.name}>
                    {option.label}
                  </RadioTile>
                ))}
              </Grid>
            </Stretch>
          </RadioGroup>
        )}
      />

      <Controller
        control={control}
        name="hasEnergy"
        render={({ field: { onChange, value, name } }) => (
          <RadioGroup
            name={name}
            direction="row"
            value={value}
            label={textualData?.choice?.hasEnergyRadio?.value?.label ?? ''}
            onValueChange={onChange}>
            <Stretch width={true}>
              <Grid gridTemplateColumns="1fr 1fr" gap={'3'}>
                {textualData?.choice?.hasEnergyRadio?.value?.options?.map(option => (
                  <RadioTile key={option.label} value={option.name}>
                    {option.label}
                  </RadioTile>
                ))}
              </Grid>
            </Stretch>
          </RadioGroup>
        )}
      />

      {notificationField?.value && (
        <Stack.Item>
          <Text size="BodyS">
            <NotificationBox
              isAlert={false}
              variant={notificationField?.value?.variant}
              title={notificationField?.value?.title}
              text={<RichText html={notificationField?.value?.content} />}
            />
          </Text>
        </Stack.Item>
      )}

      {canContinueFlow && (
        <NextButton isLoading={isSubmitSuccessful}>{textualData?.content?.nextStepText?.value}</NextButton>
      )}
    </Layout.Content>
  );
};
